import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import { offerTypeService, CreateOfferTypeDto } from "../../apis/offerTypes";

interface CreateOfferTypeModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateOfferTypeModal: React.FC<CreateOfferTypeModalProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState<CreateOfferTypeDto>({
    name: "",
    partner_percentage: 0,
    description: "",
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = name.includes("percentage") ? parseFloat(value) || 0 : value;
    setFormData((prev) => ({
      ...prev,
      [name]: numValue,
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name || formData.partner_percentage <= 0 || formData.partner_percentage > 100) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields. Partner percentage must be between 0 and 100.",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      await offerTypeService.createOfferType(formData);
      setSnackbar({
        open: true,
        message: "Offer type created successfully",
        severity: "success",
      });
      setTimeout(() => {
        onClose();
        setFormData({
          name: "",
          partner_percentage: 0,
          description: "",
          is_active: true,
        });
      }, 1500);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to create offer type",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Offer Type</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Partner Percentage"
              name="partner_percentage"
              type="number"
              value={formData.partner_percentage}
              onChange={handleChange}
              fullWidth
              required
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              helperText="Percentage of teracar commission that goes to partner (0-100)"
            />
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? "Creating..." : "Create Offer Type"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default CreateOfferTypeModal;

